import { useState } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Create a category from the category list (feature 029, FR-034).
 *
 * <p>The same shell, the same focus trap and the same Escape/click-outside dismissal as the
 * category picker, because they are the same kind of thing to an editor: a small overlay asking
 * for one decision. The picker can create a category too, in its own "New category" field — this
 * exists because an editor arriving at the list to set up a topic should not have to open an
 * unrelated problem to make one.</p>
 *
 * <p>Both routes go through the same service method, so the name rules cannot disagree between
 * them (FR-035).</p>
 */
export function AddCategoryDialog({
  isSaving,
  error,
  onSave,
  onCancel,
}: {
  isSaving: boolean;
  /**
   * A refused name, kept on screen with what was typed still in the field. A duplicate names the
   * existing category, so the message is worth reading rather than replacing with "failed".
   */
  error: string | null;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const containerRef = useFocusTrap<HTMLDivElement>(true);
  useEscapeClose(() => { if (!isSaving) onCancel(); }, true);

  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={() => { if (!isSaving) onCancel(); }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add a category"
        className="w-full max-w-sm rounded-lg bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-category-dialog"
      >
        <h2 className="mb-3 text-base font-semibold text-ink">Add a category</h2>

        <label htmlFor="add-category-name" className="mb-1 block text-sm text-ink-soft">
          Name
        </label>
        <input
          id="add-category-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          // The same 100 the column's CHECK enforces. Capping the field is the kinder half of the
          // rule — the editor cannot type a name that will be refused — and the server still
          // checks, because a cap on an input is a convenience and not a guarantee.
          maxLength={100}
          disabled={isSaving}
          autoFocus
          className="mb-3 w-full rounded border border-line px-2 py-1.5 text-sm text-ink"
          data-testid="add-category-name"
        />

        {error && (
          <p className="mb-3 text-sm text-danger" role="alert" data-testid="add-category-error">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="min-h-[24px] rounded-md px-4 py-2 text-sm font-medium text-ink hover:bg-paper disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(trimmed)}
            // Disabled on an empty name rather than letting it round-trip to be refused: the rule
            // is knowable here and the answer would never be anything else.
            disabled={isSaving || trimmed.length === 0}
            className="min-h-[24px] rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            data-testid="add-category-save"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
